import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUpRight, MapPin, Phone, Mail, MessageCircle, Instagram, Facebook } from "lucide-react";

const stallImage = "https://customer-assets-39nsmqrw.emergentagent.net/job_2e5beccf-c671-499c-98f9-d8ddbd3bc892/artifacts/t4s0mkdt_file_00000000376c81f48c11b11a981c8811.png";
const logoImage = "https://customer-assets-lqy194kg.emergentagent.net/job_rollnook-menu/artifacts/46mdts84_file_000000000c50821080767ec0df07eae0.webp";
const foodImage = "https://customer-assets-lqy194kg.emergentagent.net/job_rollnook-menu/artifacts/wi21emfl_file_00000000e6908210a655e46fcd6b0836.png";
const tawaImage = "https://customer-assets-lqy194kg.emergentagent.net/job_rollnook-menu/artifacts/rvs8enyi_file_00000000d66c81fa9df41c362e59f13f.png";
const vibeImage = "https://customer-assets-lqy194kg.emergentagent.net/job_rollnook-menu/artifacts/nrkk6jfs_file_0000000082a081f4bb1a77a53165fd67.png";
const eggRollImage = "https://customer-assets-lqy194kg.emergentagent.net/job_rollnook-menu/artifacts/misj0sjz_file_0000000000908207826d1aa3cdd2d09a.png";

const telUrl = "tel:+918250033726";
const waOrder = (item) => `https://wa.me/918250033726?text=${encodeURIComponent(`Hi RollNook, I want to order a ${item}.`)}`;
const waUrl = "https://wa.me/918250033726?text=Hi%20RollNook%2C%20I%20want%20to%20place%20an%20order.";
const mapsSearchUrl = "https://www.google.com/maps/search/?api=1&query=RollNook%2C%20Keshtopur%2C%20Kolkata";
const instagramUrl = "https://www.instagram.com/rollnook_kolkata/";
const facebookUrl = "https://www.facebook.com/share/1BC8HGrPjq/";

const menuItems = [
  { name: "Egg Roll", note: "Soft. Hot. Golden.", price: "₹40", number: "01", image: eggRollImage, position: "50% 38%", ingredients: ["Egg", "Onion", "Green chilli", "Sauce", "Paratha"] },
  { name: "Double Egg Roll", note: "Double egg. Double hit.", price: "₹50", number: "02", image: stallImage, position: "74% 68%", ingredients: ["Double egg", "Onion", "Chilli", "Sauce", "Paratha"] },
  { name: "Chicken Roll", note: "Juicy. Spiced. Loaded.", price: "₹70", number: "03", featured: true, image: vibeImage, position: "center 66%", ingredients: ["Chicken", "Egg", "Onion", "Green chilli", "Sauce", "Paratha"] },
  { name: "Double Chicken Roll", note: "Double the chicken. No mercy.", price: "₹90", number: "04", image: tawaImage, position: "center 60%", ingredients: ["Double chicken", "Egg", "Onion", "Chilli", "Sauce", "Paratha"] },
];

const whyPoints = [
  { title: "Fresh Off the Tawa", text: "Tawa → Roll → You.", number: "01" },
  { title: "Hot & Loaded", text: "Steaming. Tight. Ready.", number: "02" },
  { title: "Kolkata Street-Food Soul", text: "Real masala. Real heat.", number: "03" },
  { title: "Fast & Fresh", text: "Quick hands. Zero shortcuts.", number: "04" },
];

function App() {
  const [activeItem, setActiveItem] = useState(null);
  const [heroActive, setHeroActive] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [revealed, setRevealed] = useState(() => new Set());
  const heroFrameRef = useRef(null);
  const navRef = useRef(null);

  const rv = (id) => (revealed.has(id) ? "revealed" : "");

  useEffect(() => {
    let io = null;
    const els = Array.from(document.querySelectorAll("[data-reveal]"));
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-reveal");
            setRevealed((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08, rootMargin: "0px 0px -4% 0px" });
      els.forEach((el) => io.observe(el));
    } else {
      setRevealed(new Set(els.map((el) => el.getAttribute("data-reveal"))));
    }

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (navRef.current) navRef.current.classList.toggle("scrolled", y > 40);
        if (heroFrameRef.current) {
          heroFrameRef.current.style.setProperty("--py", `${Math.min(y * 0.16, 110)}px`);
        }
        document.querySelectorAll("[data-parallax]").forEach((el) => {
          const rect = el.getBoundingClientRect();
          const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
          el.style.setProperty("--parallax", `${progress * -46}px`);
        });
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    const fine = window.matchMedia("(pointer: fine)").matches;
    const hero = document.getElementById("top");
    const onMove = (e) => {
      if (!heroFrameRef.current || !hero) return;
      const rect = hero.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      heroFrameRef.current.style.setProperty("--ry", `${x * 7}deg`);
      heroFrameRef.current.style.setProperty("--rx", `${y * -6}deg`);
    };
    const onLeave = () => {
      if (heroFrameRef.current) {
        heroFrameRef.current.style.setProperty("--rx", "0deg");
        heroFrameRef.current.style.setProperty("--ry", "0deg");
      }
    };
    if (fine && hero) {
      hero.addEventListener("mousemove", onMove);
      hero.addEventListener("mouseleave", onLeave);
    }
    return () => {
      if (io) io.disconnect();
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      if (hero) {
        hero.removeEventListener("mousemove", onMove);
        hero.removeEventListener("mouseleave", onLeave);
      }
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (!e.target.closest(".topbar")) setMenuOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuOpen]);

  return (
    <main className="site-shell">
      <nav className="topbar" ref={navRef} data-testid="site-navigation">
        <a className="brand-mark" href="#top" data-testid="brand-home-link" aria-label="RollNook home">
          <img src={logoImage} alt="RollNook" data-testid="header-logo" />
        </a>
        <div className="nav-links">
          <a href="#menu" data-testid="nav-menu-link" onClick={(e) => { e.preventDefault(); setMenuOpen(!menuOpen); }} aria-expanded={menuOpen}>Menu</a>
          <a href="#story" className="nav-vibe" data-testid="nav-story-link">Our vibe</a>
          <a className="nav-order" href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="nav-order-button">Order <ArrowUpRight size={15} /></a>
        </div>
        {menuOpen && (
          <div className="menu-drop" data-testid="nav-menu-dropdown">
            <p className="drop-label">PICK YOUR ROLL</p>
            {menuItems.map((item) => (
              <div className="drop-row" key={item.number}><span>{item.name}</span><strong>{item.price}</strong></div>
            ))}
            <a href="#menu" className="drop-full" data-testid="dropdown-view-menu" onClick={() => setMenuOpen(false)}>View full menu <ArrowDown size={13} /></a>
          </div>
        )}
      </nav>

      <section className="hero" id="top" data-testid="hero-section">
        <div className="hero-copy">
          <p className="eyebrow hero-eyebrow" data-testid="hero-eyebrow"><span /> KOLKATA • STREET FOOD</p>
          <h1 data-testid="hero-heading">
            <span className="line"><span>Hot Rolls.</span></span>
            <span className="line"><span className="gold-line">Great Mood.</span></span>
          </h1>
          <p className="hero-intro" data-testid="hero-description">Fresh off the tawa.<br />Loaded for Kolkata.</p>
          <div className="hero-actions">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="cta-primary" data-testid="hero-order-button">Order a Roll <ArrowUpRight size={16} /></a>
            <a href="#menu" className="cta-ghost" data-testid="view-menu-button">View Menu <ArrowDown size={15} /></a>
          </div>
        </div>
        <div className="hero-visual" data-testid="hero-image-container">
          <div className="hero-glow" aria-hidden="true" />
          <div
            className={`hero-frame ${heroActive ? "roll-active" : ""}`}
            ref={heroFrameRef}
            onClick={() => setHeroActive(!heroActive)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setHeroActive(!heroActive); } }}
            role="button"
            aria-pressed={heroActive}
            tabIndex={0}
            data-testid="hero-roll-toggle"
          >
            <img className="hero-photo" src={eggRollImage} alt="A hot Kolkata-style Egg Roll with egg, onion, green chilli and sauce, steaming on a dark table" data-testid="hero-stall-image" fetchPriority="high" />
            <div className="roll-pop" aria-hidden="true">
              <img className="roll-cut" src={eggRollImage} alt="" />
            </div>
            <div className="steam" aria-hidden="true"><span /><span /><span /></div>
            <div className="hero-number">EST. <span>KESHTOPUR</span></div>
            {!heroActive && <span className="roll-cue" data-testid="hero-roll-cue">Tap the roll</span>}
            {heroActive && (
              <div className="roll-info" data-testid="hero-roll-info" onClick={(e) => e.stopPropagation()}>
                <div><h3>Egg Roll</h3><p>Soft. Hot. Golden.</p></div>
                <strong>₹40</strong>
                <a className="roll-order" href={waOrder("Egg Roll")} target="_blank" rel="noopener noreferrer" data-testid="hero-roll-order" onClick={(e) => e.stopPropagation()}>Order on WhatsApp <ArrowUpRight size={13} /></a>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          <span>Hot Rolls • Great Mood • Fresh Off The Tawa • Keshtopur • Kolkata •&nbsp;</span>
          <span>Hot Rolls • Great Mood • Fresh Off The Tawa • Keshtopur • Kolkata •&nbsp;</span>
        </div>
      </div>

      <section className="menu-section section-wrap" id="menu" data-testid="menu-section">
        <div className={`section-heading ${rv("menu-head")}`} data-reveal="menu-head">
          <div>
            <p className="eyebrow red"><span /> THE GOOD STUFF</p>
            <h2 data-testid="menu-heading">Pick your<br /><em>mood.</em></h2>
          </div>
          <p className="section-note">Tap a roll.<br />See what&rsquo;s inside.</p>
        </div>
        <div className="menu-grid" data-testid="menu-list">
          {menuItems.map((item, i) => (
            <article
              className={`menu-card ${activeItem === item.number ? "active" : ""} ${item.featured ? "featured" : ""} ${rv(`card-${item.number}`)}`}
              key={item.name}
              data-testid={`menu-item-${item.number}`}
              data-reveal={`card-${item.number}`}
              style={{ transitionDelay: revealed.has(`card-${item.number}`) ? "0ms" : `${i * 60}ms` }}
              onClick={() => setActiveItem(activeItem === item.number ? null : item.number)}
            >
              <div className="card-media">
                <div className="media-clip">
                  <img className="media-img media-bg" src={item.image} alt={`${item.name} at RollNook`} style={{ objectPosition: item.position }} loading="lazy" decoding="async" />
                  <div className="card-shade" aria-hidden="true" />
                </div>
                <div className="media-pop" aria-hidden="true">
                  <img className="media-img media-fg" src={item.image} style={{ objectPosition: item.position }} alt="" loading="lazy" decoding="async" />
                </div>
                <span className="card-number">{item.number}</span>
              </div>
              <div className="card-body">
                <div className="card-copy">
                  <h3>{item.name}</h3>
                  <p>{item.note}</p>
                </div>
                <strong className="item-price" data-testid={`menu-price-${item.number}`}>{item.price}</strong>
              </div>
              {activeItem === item.number && (
                <div className="card-active-zone">
                  <p className="card-status">Hot. Loaded. Ready.</p>
                  <a className="card-order" href={waOrder(item.name)} target="_blank" rel="noopener noreferrer" data-testid={`menu-order-${item.number}`} onClick={(e) => e.stopPropagation()}>
                    Order on WhatsApp <ArrowUpRight size={14} />
                  </a>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="food-feature" data-testid="food-feature-section">
        <img src={tawaImage} alt="Fresh RollNook chicken rolls stacked at the stall while the cook works the tawa" data-testid="food-feature-image" data-parallax loading="lazy" decoding="async" />
        <div className="food-overlay" aria-hidden="true" />
        <div className={`food-caption ${rv("food-caption")}`} data-reveal="food-caption">
          <p className="eyebrow red"><span /> HOT OFF THE TAWA</p>
          <h2 className="food-heading" data-testid="food-heading">Made fresh.<br /><em>Served hot.</em></h2>
          <p className="food-copy" data-testid="food-copy">Eggs. Chicken. Onions. Spices. Wrapped fresh, right off the tawa.</p>
        </div>
      </section>

      <section className="story-section section-wrap" id="story" data-testid="story-section">
        <div className={`story-head ${rv("story-head")}`} data-reveal="story-head">
          <p className="eyebrow"><span /> WHY ROLLNOOK</p>
          <h2>One bite.<br /><em>Instant mood upgrade.</em></h2>
        </div>
        <div className={`story-image ${rv("story-image")}`} data-reveal="story-image">
          <img src={foodImage} alt="Fresh eggs, onions, green chillies and spices cooking on the hot RollNook tawa" data-testid="story-vibe-image" loading="lazy" decoding="async" />
        </div>
        <div className="why-list" data-testid="why-list">
          {whyPoints.map((point, i) => (
            <div className={`why-row ${rv(`why-${point.number}`)}`} key={point.number} data-testid={`why-point-${point.number}`} data-reveal={`why-${point.number}`} style={{ transitionDelay: revealed.has(`why-${point.number}`) ? "0ms" : `${i * 70}ms` }}>
              <span className="why-num">{point.number}</span>
              <div>
                <h3>{point.title}</h3>
                <p>{point.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="location-section section-wrap" id="location" data-testid="location-section">
        <div className={rv("loc-head")} data-reveal="loc-head">
          <p className="eyebrow red"><span /> FIND THE FLAME</p>
          <h2 data-testid="location-heading">The flame<br /><em>lives here.</em></h2>
        </div>
        <div className={`location-card ${rv("loc-card")}`} data-testid="location-card" data-reveal="loc-card">
          <MapPin size={23} />
          <div>
            <strong data-testid="location-address">KESHTOPUR</strong>
            <p>Kolkata, West Bengal</p>
            <a className="maps-link" href={mapsSearchUrl} target="_blank" rel="noopener noreferrer" data-testid="location-maps-link">Come find the heat <ArrowUpRight size={13} /></a>
          </div>
          <span className="location-dot" />
        </div>
      </section>

      <section className="contact-section section-wrap" id="contact" data-testid="contact-section">
        <div className={rv("contact-head")} data-reveal="contact-head">
          <p className="eyebrow red"><span /> ORDER &amp; CONTACT</p>
          <h2 data-testid="contact-heading">Ready for<br /><em>a roll?</em></h2>
          <p className="contact-sub" data-testid="contact-subline">Hot rolls.<br />One message away.</p>
        </div>
        <div className={`contact-details ${rv("contact-card")}`} data-reveal="contact-card">
          <a className="call-button" href={telUrl} data-testid="contact-call-button"><Phone size={18} /> CALL TO ORDER — 8250033726</a>
          <a className="whatsapp-button" href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button"><MessageCircle size={18} /> WHATSAPP US</a>
          <p className="contact-label">EMAIL</p>
          <a className="contact-email" href="mailto:sagar.kheyal601355@gmail.com" data-testid="contact-email-link"><Mail size={14} /> sagar.kheyal601355@gmail.com</a>
        </div>
      </section>

      <section className="final-cta" data-testid="final-cta-section">
        <h2 className={rv("cta-head")} data-testid="final-cta-heading" data-reveal="cta-head">Come hungry.<br /><em>Leave happy.</em></h2>
        <p className={rv("cta-lines")} data-testid="final-cta-lines" data-reveal="cta-lines">ROLLNOOK • HOT ROLLS. GREAT MOOD.</p>
      </section>

      <footer className="footer" data-testid="site-footer">
        <div className="footer-brand"><img src={logoImage} alt="RollNook" data-testid="footer-logo" /><p>ROLLNOOK — HOT ROLLS. GREAT MOOD.</p></div>
        <a className="footer-order" href={telUrl} data-testid="contact-order-button">Contact / Order <ArrowUpRight size={18} /></a>
        <div className="social-links" data-testid="footer-social-links">
          <a className="social-link" href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="RollNook on Instagram" data-testid="footer-instagram-link"><Instagram size={18} /></a>
          <a className="social-link" href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="RollNook on Facebook" data-testid="footer-facebook-link"><Facebook size={18} /></a>
        </div>
        <p className="footer-note">MADE FRESH IN KOLKATA.</p>
      </footer>
    </main>
  );
}

export default App;
